import { BarsArrowUpIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { useBumpVehicleMutation, Vehicle } from "../graphql/generated/graphql";
import { TIER_KEYS } from "../utils/constants";
import Spinner from "./Spinner";
import {
  useGetVehicleQuery,
  GetVehicleDocument,
} from "../graphql/generated/graphql";

type TProps = {
  vehicle: Vehicle;
  open?: boolean;
  setOpen: (open?: boolean) => void;
};

function BumpVehicleModal({ vehicle, open, setOpen }: TProps) {
  const [isBumped, setIsBumped] = useState(false);

  // Refetching vehicle data here cause otherwise the cache.updateQuery below won't work
  const { data } = useGetVehicleQuery({
    variables: {
      _id: vehicle._id,
    },
  });
  const { getVehicle } = data || {};

  const { _id: vehicleId, bumpCount, listingTier } = getVehicle || {};
  const isBumpEnabled = listingTier === TIER_KEYS.MAX && bumpCount < 2;
  // (listingTier === TIER_KEYS.PLUS && bumpCount < 1) ||
  const remainingBumps = 2 - bumpCount;
  // listingTier === TIER_KEYS.PLUS ? 1 - bumpCount : 2 - bumpCount;

  const [bumpVehicle, { loading: bumping }] = useBumpVehicleMutation({
    onCompleted: () => {
      setIsBumped(true);
    },
    update(cache, { data: { bumpVehicle } }) {
      cache.updateQuery(
        { query: GetVehicleDocument, variables: { _id: vehicle._id } },
        (data) => {
          ({
            getVehicle: {
              ...vehicle,
              bumpCount: bumpVehicle.bumpCount,
            },
          });
        }
      );
    },
  });

  function applyBump() {
    bumpVehicle({
      variables: {
        _id: vehicleId,
      },
    });
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div className="text-center">
                  <Dialog.Title as="h3" className="leading-6 ">
                    Dar empujón
                  </Dialog.Title>
                  <p className=" text-sm text-gray-500">
                    Dale un empujón a tu vehículo para que aparezca
                    temporalmente entre los primeros resultados de búsqueda.
                    Útil para cuando tu publicación ya tiene unos cuantos días o
                    semanas en el sitio y querés darle mayor visibilidad.
                  </p>
                </div>
                <div className="mt-2 flex flex-col gap-x-1">
                  <div className="mt-2">
                    <h3 className="font-bold text-lg">
                      Empujones restantes: {remainingBumps}
                    </h3>
                  </div>
                  <button
                    disabled={!isBumpEnabled || isBumped}
                    onClick={applyBump}
                    className="mt-2 inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-orange-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
                  >
                    {bumping ? <Spinner className="h-5 w-5" /> : null}
                    {isBumped ? "Empujón exitoso!" : "Dar empujón"}
                  </button>

                  <button
                    type="button"
                    className="mt-2 inline-flex w-full justify-center rounded-md border border-gray-300 hover:border-gray-400 bg-white px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-600 shadow-sm focus:outline-none focus:ring-2  focus:ring-offset-2 sm:text-sm"
                    onClick={() => setOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default function BumpVehicleButton({
  loading,
  data,
}: {
  loading?: boolean;
  asIcon?: boolean;
  data: Vehicle;
}) {
  const [isBumpModalOpen, setBumpModalOpen] = useState(false);

  function toggleBumpModal() {
    setBumpModalOpen(!isBumpModalOpen);
  }

  return (
    <>
      <button
        disabled={loading}
        className="-ml-px relative inline-flex items-center space-x-2 rounded-md border-2 border-blue-500 bg-blue-500 px-3 sm:px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-600 hover:border-blue-600 disabled:opacity-70"
        onClick={toggleBumpModal}
      >
        <BarsArrowUpIcon className="h-5 w-5" />
        {/* <PencilIcon className="h-5 w-5" aria-hidden="true" /> */}
        <span className="hidden sm:inline">Dar empujón</span>
      </button>
      <BumpVehicleModal
        open={isBumpModalOpen}
        setOpen={setBumpModalOpen}
        vehicle={data}
      />
    </>
  );
}
